import { ListPlus } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import { type ReperformanceStep } from "../../../../../models/audit/attributeTestColumn";
import { type Citation } from "../../../../../models/audit/citation";
import { cn } from "../../../../../lib/utils";
import { scrollToVerticalCenter } from "../../../../../lib/scrollToCenter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../../components/ui/dropdown-menu";
import {
  type AgentStepGroup,
  groupAgentCitationsByStep,
} from "../../lib/agentCitations";

/** A review-trail (L2) step a citation can be imported into. */
export interface ReviewStepOption {
  id: string;
  text: string;
}

interface AgentStepsPanelProps {
  reperformanceSteps: ReperformanceStep[];
  /** Show a citation's document in the viewer (drives the right pane). */
  onViewEvidence?: (citation: Citation) => void;
  /** The citation currently shown in the viewer, for active styling. */
  activeCitationId?: string;
  /** Decides which member of a source/screenshot pair is the screenshot view. */
  isScreenshotDoc?: (documentId: string | null | undefined) => boolean;
  /** Copy an agent citation onto a review-trail step. Enables the per-citation
   * "add to review" affordance when provided alongside `reviewSteps`. */
  onImportToReview?: (sourceCitationId: string, auditorStepId: string) => void;
  /** The review-trail steps a citation can be imported into. */
  reviewSteps?: ReviewStepOption[];
}

/**
 * Read-only, verbose list of the agent's reperformance citations grouped by
 * step. The middle pane's "Workpaper Review ↔ Agent Steps" toggle swaps to this
 * view. Each step shows its title as a header, then the citations it relied on
 * (source/screenshot pairs collapsed); the same evidence reused across steps
 * appears under each step. Clicking a citation drives the doc viewer, and the
 * active citation's step is scroll-centred (e.g. when a tickmark is clicked in
 * the viewer or via keyboard nav).
 */
export function AgentStepsPanel({
  reperformanceSteps,
  onViewEvidence,
  activeCitationId,
  isScreenshotDoc = () => false,
  onImportToReview,
  reviewSteps,
}: AgentStepsPanelProps) {
  const stepGroups = useMemo(
    () => groupAgentCitationsByStep(reperformanceSteps, isScreenshotDoc),
    [reperformanceSteps, isScreenshotDoc],
  );

  const scrollRef = useRef<HTMLDivElement>(null);

  // Centre the step that owns the active citation whenever it changes from
  // outside (e.g. a tickmark click in the doc viewer or keyboard nav), so the
  // highlighted evidence is always in view.
  useEffect(() => {
    if (!activeCitationId) return;
    const owning = stepGroups.find((group) =>
      group.items.some(
        (item) =>
          item.source.id === activeCitationId ||
          item.screenshot?.id === activeCitationId,
      ),
    );
    const container = scrollRef.current;
    if (!owning || !container) return;
    const timer = window.setTimeout(() => {
      const card = container.querySelector(`[data-step-key="${owning.key}"]`);
      if (card instanceof HTMLElement) scrollToVerticalCenter(container, card);
    }, 50);
    return () => window.clearTimeout(timer);
  }, [activeCitationId, stepGroups]);

  if (stepGroups.length === 0) {
    return (
      <div className="flex h-full items-center justify-center px-4 text-sm text-stone-400">
        No agent citations recorded for this result.
      </div>
    );
  }

  const citationCount = stepGroups.reduce(
    (total, group) => total + group.items.length,
    0,
  );

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto px-4 py-3">
      <div className="mb-2 text-xs text-stone-400">
        {citationCount} {citationCount === 1 ? "citation" : "citations"} across{" "}
        {stepGroups.length} {stepGroups.length === 1 ? "step" : "steps"}
      </div>
      <ol className="space-y-3">
        {stepGroups.map((group) => (
          <AgentStepGroupCard
            key={group.key}
            group={group}
            onViewEvidence={onViewEvidence}
            activeCitationId={activeCitationId}
            onImportToReview={onImportToReview}
            reviewSteps={reviewSteps}
          />
        ))}
      </ol>
    </div>
  );
}

function AgentStepGroupCard({
  group,
  onViewEvidence,
  activeCitationId,
  onImportToReview,
  reviewSteps,
}: {
  group: AgentStepGroup;
  onViewEvidence?: (citation: Citation) => void;
  activeCitationId?: string;
  onImportToReview?: (sourceCitationId: string, auditorStepId: string) => void;
  reviewSteps?: ReviewStepOption[];
}) {
  return (
    <li
      data-step-key={group.key}
      className="rounded-md border border-stone-200"
    >
      {/* Step header: the title of this group of citations. */}
      <div className="flex items-baseline gap-1.5 border-b border-stone-100 px-2.5 py-2">
        <span className="shrink-0 text-[11px] font-semibold uppercase tracking-wide text-stone-700">
          Step {group.stepIndex + 1}
        </span>
        <span className="min-w-0 truncate text-xs font-medium text-stone-700">
          {group.stepName}
        </span>
      </div>

      {/* The step's citations. */}
      <ul className="space-y-0.5 p-1.5">
        {group.items.map((item) => (
          <CitationRow
            key={item.source.id}
            sourceId={item.source.id}
            summary={item.source.shortSummary}
            active={
              item.source.id === activeCitationId ||
              item.screenshot?.id === activeCitationId
            }
            onView={() => onViewEvidence?.(item.screenshot ?? item.source)}
            onImportToReview={onImportToReview}
            reviewSteps={reviewSteps}
          />
        ))}
      </ul>
    </li>
  );
}

function CitationRow({
  sourceId,
  summary,
  active,
  onView,
  onImportToReview,
  reviewSteps,
}: {
  sourceId: string;
  summary: string | null;
  active: boolean;
  onView: () => void;
  onImportToReview?: (sourceCitationId: string, auditorStepId: string) => void;
  reviewSteps?: ReviewStepOption[];
}) {
  const canImport =
    !!onImportToReview && !!reviewSteps && reviewSteps.length > 0;

  return (
    <li className="flex items-start gap-1">
      <button
        type="button"
        onClick={onView}
        className={cn(
          "flex min-w-0 flex-1 items-start gap-2 rounded-md px-2 py-1 text-left text-xs transition-colors",
          active
            ? "bg-stone-100 text-stone-900"
            : "text-stone-600 hover:bg-stone-50 hover:text-stone-900",
        )}
      >
        <span
          className={cn(
            "mt-1 h-1.5 w-1.5 shrink-0 rounded-full",
            active ? "bg-stone-500" : "bg-stone-300",
          )}
        />
        <span className="min-w-0 flex-1">{summary || "Cited evidence"}</span>
      </button>

      {/* Import this agent citation into a review-trail step. */}
      {canImport && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              title="Add to Workpaper Review"
              className="mt-0.5 shrink-0 rounded-md p-1 text-stone-400 transition-colors hover:bg-stone-100 hover:text-stone-600"
            >
              <ListPlus className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-80">
            <DropdownMenuLabel className="text-xs font-normal text-stone-500">
              Add to Workpaper Review step
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {reviewSteps!.map((step, index) => (
              <DropdownMenuItem
                key={step.id}
                onSelect={() => onImportToReview!(sourceId, step.id)}
                className="items-start gap-2 text-xs"
              >
                <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-stone-300 bg-white text-xs font-semibold text-stone-900 shadow-sm">
                  {index + 1}
                </span>
                <span className="whitespace-normal">{step.text}</span>
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </li>
  );
}
